/**
 * HITO 3-Week Look-Ahead (3WLA) workbook model.
 *
 * Layout and CF match `3WLA REV Clean.xlsm` sheet `3 week` (v1: values + CF,
 * no VBA / ActiveX / 1 Year / 3 Month / to P6). Date *window* for default
 * activity inclusion matches FilterLogic Next 3 Weeks (Monday–Sunday,
 * TK_Active always kept). Gantt *columns* follow the template (Monday weeks,
 * "This week" = week of the data date).
 */

import dayjs from './date.js';
import {
	calendarForActivity,
	holidayYmds,
	isWorkDay,
	oleSerialToYmd,
	paintsDay,
	projectCalendar,
	workingWeekdayCount,
	ymdToOleSerial
} from './workCalendar.js';

export const SHEET_NAME = '3 week';
export const INFO_SHEET_NAME = 'Information Sheet';

/** First / last columns of the full H–CJ day strip (template A–CN). */
export const FIRST_COL = 1;
export const LAST_COL = 92; // CN
export const GANTT_FIRST_COL = 8; // H
export const GANTT_LAST_COL = 88; // CJ
export const COL = {
	band: 1, // A (hidden) — Blue / Light Blue / Grey / Light Brown
	activityId: 2, // B (hidden)
	description: 3, // C
	pctComp: 4, // D
	remDur: 5, // E
	start: 6, // F (hidden; CF uses this)
	finish: 7, // G (hidden; CF uses this)
	comments: 89, // CK
	crewSize: 90, // CL
	currentWeek: 91, // CM
	critical: 92 // CN
};

export const HEADER_ROWS = 6;
export const FIRST_DATA_ROW = 7;

export const BAND_KEYWORDS = ['Blue', 'Light Blue', 'Grey', 'Light Brown'];

/**
 * Office theme fills from 3WLA REV Clean.xlsm (spreadsheet theme index:
 * 0=lt1, 1=dk1, 2=lt2, 3=dk2, 4=accent1, 5=accent2, 8=accent5) plus tint.
 * Hex is baked so ExcelJS CF and static fallbacks match without theme XML.
 */
export const COLORS = {
	blue: '7992B1',
	lightBlue: '93CDDD',
	grey: 'BFBFBF',
	lightBrown: '777671',
	lime: '00FF00',
	critical: 'C0504D',
	weekend: '808080',
	dataDateRed: 'FF0000',
	headerFill: 'FFFFFF99'
};

export const COLOR_BY_BAND = {
	Blue: COLORS.blue,
	'Light Blue': COLORS.lightBlue,
	Grey: COLORS.grey,
	'Light Brown': COLORS.lightBrown
};

/** Template cols hidden: A, B, F, G, H–K (3 weeks ago stub), AU–CJ (week 4+). */
export const HIDDEN_COLS = [1, 2, 6, 7]
	.concat(range(8, 11))
	.concat(range(47, 88));

export const COLUMN_WIDTHS = {
	1: 12.7,
	2: 23.7,
	3: 87.3,
	4: 12.7,
	5: 12.4,
	6: 15.7,
	7: 15.7,
	89: 53.3,
	90: 11.3,
	91: 14.3,
	92: 9.3
};

export const DAY_COL_WIDTH = 4.43;

export const WEEK_LABELS = [
	{ col: 8, label: '3 weeks ago', days: 4 },
	{ col: 12, label: 'Last Week', days: 7 },
	{ col: 19, label: 'This week', days: 7 },
	{ col: 26, label: 'Next week', days: 7 },
	{ col: 33, label: '2 Weeks Ahead', days: 7 },
	{ col: 40, label: '3 Weeks Ahead', days: 7 },
	{ col: 47, label: '4 Weeks Ahead', days: 7 },
	{ col: 54, label: '5 weeks ahead', days: 7 },
	{ col: 61, label: '6 weeks ahead', days: 7 },
	{ col: 68, label: '7 weeks ahead', days: 7 },
	{ col: 75, label: '8 weeks ahead', days: 7 },
	{ col: 82, label: '9 weeks ahead', days: 7 }
];

export const MERGES = [
	'S1:Y1', 'E2:F2', 'S2:Y2',
	'E4:F4', 'H4:K4', 'L4:R4', 'S4:Y4', 'Z4:AF4', 'AG4:AM4', 'AN4:AT4',
	'AU4:BA4', 'BB4:BH4', 'BI4:BO4', 'BP4:BV4', 'BW4:CC4', 'CD4:CJ4'
];

function range (from, to) {
	const out = [];
	for (let i = from; i <= to; i++) {
		out.push(i);
	}
	return out;
}

export function colLetter (n) {
	let s = '';
	let x = n;
	while (x > 0) {
		const r = (x - 1) % 26;
		s = String.fromCharCode(65 + r) + s;
		x = Math.floor((x - 1) / 26);
	}
	return s;
}

/** Excel 1900-date serial for a local calendar day (matches the template). */
export function toExcelSerial (ms) {
	if (!ms) {
		return null;
	}
	const d = dayjs(ms).startOf('day');
	if (!d.isValid()) {
		return null;
	}
	const epoch = dayjs('1899-12-30').startOf('day');
	return d.diff(epoch, 'day');
}

export function startOfDayMs (ms) {
	const n = Number(ms) || 0;
	if (!n) {
		return 0;
	}
	return dayjs(n).startOf('day').valueOf();
}

/**
 * FilterLogic Next 3 Weeks, but anchored on the schedule data date
 * instead of wall-clock today. Monday–Sunday; skips the current week.
 */
export function nextThreeWeeksWindow (dataDateMs) {
	const d = dayjs(dataDateMs || Date.now());
	return {
		start: d.add(7, 'days').startOf('week').valueOf(),
		end: d.add(21, 'days').endOf('week').valueOf()
	};
}

export function hasActiveDateFilter (projectFilter) {
	if (!projectFilter) {
		return false;
	}
	if (projectFilter.useDynamicDates && projectFilter.dynamicRange) {
		return true;
	}
	return !!(projectFilter.startDate || projectFilter.endDate);
}

/**
 * Keep TK_Active even when it falls outside the window (FilterLogic
 * Next 2/3 Weeks exception).
 */
export function activityInLookAheadWindow (activity, startMs, endMs) {
	if (!activity) {
		return false;
	}
	if (activity.statusCode === 'TK_Active') {
		return true;
	}
	const start = Number(activity.start) || 0;
	const finish = Number(activity.finish) || 0;
	if (startMs && finish && finish < startMs) {
		return false;
	}
	if (endMs && start && start > endMs) {
		return false;
	}
	return true;
}

export function resolveDataDateMs (dataset) {
	const projects = dataset && dataset.projects && !Array.isArray(dataset.projects)
		? dataset.projects
		: null;
	if (projects) {
		const preferred = [];
		if (dataset.primaryProjId != null && projects[dataset.primaryProjId]) {
			preferred.push(String(dataset.primaryProjId));
		}
		Object.keys(projects).forEach(id => {
			if (preferred.indexOf(String(id)) === -1) preferred.push(String(id));
		});
		for (const id of preferred) {
			const raw = projects[id] && projects[id].dataDate;
			if (raw && raw !== '0000-00-00' && raw !== '0000-00-00 00:00:00') {
				const parsed = dayjs(raw);
				if (parsed.isValid()) {
					return parsed.startOf('day').valueOf();
				}
			}
		}
	}
	if (dataset && dataset.StartDate) {
		return startOfDayMs(dataset.StartDate);
	}
	return dayjs().startOf('day').valueOf();
}

export function isActivityCritical (activity, dataset) {
	if (!activity) {
		return false;
	}
	if (activity.drivingPathFlag === 'Y') {
		return true;
	}
	const projects = dataset && dataset.projects && !Array.isArray(dataset.projects)
		? dataset.projects
		: null;
	const meta = (projects && activity.projId && projects[activity.projId]) || {};
	const threshold = Number(meta.criticalDrtnHrCnt) || 0;
	return Number(activity.totalFloat) < threshold;
}

export function remainingDurationDisplay (activity, dataset) {
	if (!activity || activity.remainingDuration == null || activity.remainingDuration === '') {
		return null;
	}
	const hours = Number(activity.remainingDuration);
	if (Number.isNaN(hours)) {
		return null;
	}
	const divisor = Number(dataset && dataset.HoursPerDay) || 1;
	return Math.round((hours / divisor) * 100) / 100;
}

/**
 * H5 = data_date - WEEKDAY(data_date, 3) - 4 - 7
 * WEEKDAY(..., 3) is Monday=0 … Sunday=6, so this is Thursday 11 days
 * before the Monday of the data-date week (template "3 weeks ago" stub).
 */
export function ganttOriginSerial (dataDateMs) {
	const serial = toExcelSerial(dataDateMs);
	const mondayOffset = dayjs(dataDateMs).day() === 0 ? 6 : dayjs(dataDateMs).day() - 1;
	return serial - mondayOffset - 4 - 7;
}

export function daySerialAt (dataDateMs, ganttIndex) {
	return ganttOriginSerial(dataDateMs) + ganttIndex;
}

export function isWeekendOrHoliday (serial, daysPerWeek, holidaySerials) {
	// WEEKDAY(serial, 2): Monday=1 … Sunday=7. Excel serial 1 is Sunday 1900-01-01
	// but we use JS: day() Sunday=0. Convert via the same calendar day.
	const epoch = dayjs('1899-12-30').startOf('day');
	const d = epoch.add(serial, 'day');
	const weekdayMon1 = d.day() === 0 ? 7 : d.day();
	if (weekdayMon1 > daysPerWeek) {
		return true;
	}
	return (holidaySerials || []).indexOf(serial) !== -1;
}

function countTreeActivities (item, activitiesMap) {
	if (!item || !activitiesMap) {
		return 0;
	}
	let count = 0;
	const ids = item.activityIds || [];
	for (let i = 0; i < ids.length; i++) {
		if (ids[i] in activitiesMap) {
			count++;
		}
	}
	const children = item.children || {};
	const seq = item.childrenSequence || [];
	for (let i = 0; i < seq.length; i++) {
		if (seq[i] in children) {
			count += countTreeActivities(children[seq[i]], activitiesMap);
		}
	}
	return count;
}

function appendTreeRows (item, depth, activitiesMap, out) {
	if (!item) {
		return;
	}
	if (!countTreeActivities(item, activitiesMap)) {
		return;
	}
	const keyword = BAND_KEYWORDS[Math.min(depth, BAND_KEYWORDS.length - 1)];
	const title = item.wbsName || item.name || '';
	out.push({
		kind: 'band',
		keyword,
		description: title
	});
	const ids = item.activityIds || [];
	for (let i = 0; i < ids.length; i++) {
		const activity = activitiesMap[ids[i]];
		if (activity) {
			out.push({ kind: 'activity', activity });
		}
	}
	const children = item.children || {};
	const seq = item.childrenSequence || [];
	for (let i = 0; i < seq.length; i++) {
		if (seq[i] in children) {
			appendTreeRows(children[seq[i]], depth + 1, activitiesMap, out);
		}
	}
}

/**
 * Banded rows from WBS (default) or activity-code discipline tree.
 * Empty sections are omitted, same as WBSTree / GroupByCodeTree.
 */
export function buildGroupedRows ({
	activities,
	wbsData,
	activityCodesTree,
	groupByActivityCode,
	preferCodes
}) {
	const activitiesMap = {};
	(activities || []).forEach(activity => {
		if (activity && activity.activityId != null) {
			activitiesMap[activity.activityId] = activity;
		}
	});

	const out = [];
	if (preferCodes && groupByActivityCode && activityCodesTree && activityCodesTree[groupByActivityCode]) {
		const tree = activityCodesTree[groupByActivityCode];
		const children = tree.children || {};
		const seq = tree.childrenSequence || [];
		for (let i = 0; i < seq.length; i++) {
			if (seq[i] in children) {
				appendTreeRows(children[seq[i]], 0, activitiesMap, out);
			}
		}
		if (out.length) {
			return out;
		}
	}

	if (wbsData) {
		for (const wbsId in wbsData) {
			if (Object.prototype.hasOwnProperty.call(wbsData, wbsId)) {
				appendTreeRows(wbsData[wbsId], 0, activitiesMap, out);
			}
		}
		if (out.length) {
			return out;
		}
	}

	return (activities || []).map(activity => ({ kind: 'activity', activity }));
}

export function selectExportActivities ({
	filteredActivities,
	projectFilter,
	dataDateMs
}) {
	const source = Array.isArray(filteredActivities) ? filteredActivities : [];
	if (hasActiveDateFilter(projectFilter)) {
		return source.slice();
	}
	const window = nextThreeWeeksWindow(dataDateMs);
	return source.filter(activity => activityInLookAheadWindow(activity, window.start, window.end));
}

export function activityOverlapsSerial (activity, serial, cal) {
	if (!activity) {
		return false;
	}
	const start = toExcelSerial(activity.start);
	const finish = toExcelSerial(activity.finish) || start;
	if (start == null) {
		return false;
	}
	const isMile = activity.taskType === 'TT_Mile' || activity.taskType === 'TT_FinMile';
	if (isMile) {
		const pin = activity.taskType === 'TT_FinMile' ? (finish || start) : (start || finish);
		return pin === serial;
	}
	if (!cal) {
		return start <= serial && finish >= serial;
	}
	const ymd = oleSerialToYmd(serial);
	return paintsDay(ymd, activity.start, activity.finish || activity.start, cal);
}

/**
 * Conditional-formatting rules that ExcelJS will emit. Formulas use the
 * same anchors as the template so an edit to Start/Finish/Critical recolors.
 */
export function buildConditionalFormattingRules (lastRow) {
	const end = Math.max(lastRow, FIRST_DATA_ROW);
	const bandRef = `A6:CK${end}`;
	const weekendRef = `H6:CK${end}`;
	const ganttRef = `H${FIRST_DATA_ROW + 1}:CJ${end}`;
	return [
		{
			ref: bandRef,
			type: 'expression',
			formula: 'IF($A6="Blue",TRUE,FALSE)',
			priority: 2,
			fill: COLORS.blue,
			bold: true,
			id: 'band-blue'
		},
		{
			ref: bandRef,
			type: 'expression',
			formula: 'IF($A6="Light Blue",TRUE,FALSE)',
			priority: 3,
			fill: COLORS.lightBlue,
			bold: true,
			id: 'band-light-blue'
		},
		{
			ref: bandRef,
			type: 'expression',
			formula: 'IF($A6="Grey",TRUE,FALSE)',
			priority: 4,
			fill: COLORS.grey,
			bold: true,
			id: 'band-grey'
		},
		{
			ref: bandRef,
			type: 'expression',
			formula: 'IF($A6="Light Brown",TRUE,FALSE)',
			priority: 5,
			fill: COLORS.lightBrown,
			bold: true,
			id: 'band-light-brown'
		},
		{
			ref: weekendRef,
			type: 'expression',
			formula: 'H$3',
			priority: 6,
			fill: COLORS.weekend,
			id: 'weekend-grey'
		},
		{
			ref: ganttRef,
			type: 'expression',
			formula: 'AND(OR(H$5>=$F8,I$5>$F8),OR(H$5<=$G8,I$5<$G8),$CN8="Y",NOT(H$3))',
			priority: 204,
			fill: COLORS.critical,
			id: 'gantt-critical'
		},
		{
			ref: ganttRef,
			type: 'expression',
			formula: 'AND(OR(H$5>=$F8,I$5>$F8),OR(H$5<=$G8,I$5<$G8),NOT(H$3))',
			priority: 205,
			fill: COLORS.lime,
			id: 'gantt-lime'
		},
		{
			ref: ganttRef,
			type: 'expression',
			formula: 'AND(H$5<=(data_date+0.35),I$5>data_date+0.35)',
			priority: 206,
			pattern: 'darkGray',
			fg: COLORS.dataDateRed,
			id: 'data-date-stripe'
		}
	];
}

export function lookAheadCalendar (dataset) {
	return projectCalendar(dataset);
}

export function buildWorkbookModel (options) {
	const dataset = options.dataset || {};
	const dataDateMs = options.dataDateMs || resolveDataDateMs(dataset);
	const projectCal = lookAheadCalendar(dataset);
	const daysPerWeek = options.daysPerWeek || workingWeekdayCount(projectCal);
	const fromCalHrs = Number(projectCal && projectCal.dayHrCnt);
	const hoursPerDay = fromCalHrs > 0
		? fromCalHrs
		: (Number(dataset.HoursPerDay) > 0 ? Number(dataset.HoursPerDay) : 8);
	const holidaySerials = (options.holidaySerials && options.holidaySerials.length)
		? options.holidaySerials
		: holidayYmds(projectCal).map(ymd => ymdToOleSerial(ymd));
	const projectName = dataset.Name || 'Project';

	const activities = selectExportActivities({
		filteredActivities: options.filteredActivities,
		projectFilter: options.projectFilter,
		dataDateMs
	});

	const preferCodes = options.projectViewType === 'Group List' &&
		options.projectGroupBy === 'Activity Code';

	const grouped = buildGroupedRows({
		activities,
		wbsData: options.wbsData,
		activityCodesTree: options.projectActivityCodesTree,
		groupByActivityCode: options.projectGroupByActivityCode,
		preferCodes
	});

	const origin = ganttOriginSerial(dataDateMs);
	const dataDateSerial = toExcelSerial(dataDateMs);
	const dayCount = GANTT_LAST_COL - GANTT_FIRST_COL + 1;
	const days = [];
	for (let i = 0; i < dayCount; i++) {
		const serial = origin + i;
		days.push({
			col: GANTT_FIRST_COL + i,
			serial,
			weekend: !isWorkDay(oleSerialToYmd(serial), projectCal)
		});
	}

	const rows = grouped.map(entry => {
		if (entry.kind === 'band') {
			return {
				kind: 'band',
				keyword: entry.keyword,
				description: entry.description,
				fill: COLOR_BY_BAND[entry.keyword]
			};
		}
		const activity = entry.activity;
		const startSerial = toExcelSerial(activity.start);
		const finishSerial = toExcelSerial(activity.finish) || startSerial;
		const critical = isActivityCritical(activity, dataset);
		const activityCal = calendarForActivity(activity, dataset);
		const ganttFills = {};
		days.forEach(day => {
			if (activityOverlapsSerial(activity, day.serial, activityCal)) {
				ganttFills[day.col] = critical ? COLORS.critical : COLORS.lime;
			} else if (day.serial === dataDateSerial) {
				ganttFills[day.col] = null;
			}
		});
		return {
			kind: 'activity',
			keyword: '',
			activityId: activity.activityId,
			taskCode: activity.taskCode || '',
			description: [activity.taskCode, activity.name].filter(Boolean).join('  '),
			pctComp: activity.percentComplete == null || activity.percentComplete === ''
				? null
				: Number(activity.percentComplete) / 100,
			remDur: remainingDurationDisplay(activity, dataset),
			startSerial,
			finishSerial,
			comments: '',
			critical: critical ? 'Y' : 'N',
			ganttFills,
			statusCode: activity.statusCode
		};
	});

	const lastRow = HEADER_ROWS + Math.max(rows.length, 1);
	return {
		projectName,
		dataDateMs,
		dataDateSerial,
		daysPerWeek,
		hoursPerDay,
		holidaySerials,
		days,
		rows,
		lastRow,
		cfRules: buildConditionalFormattingRules(lastRow),
		window: nextThreeWeeksWindow(dataDateMs),
		appliedDefaultWindow: !hasActiveDateFilter(options.projectFilter),
		activityCount: rows.filter(r => r.kind === 'activity').length,
		bandCount: rows.filter(r => r.kind === 'band').length
	};
}
